/**
 * Security: sensitive data seen in prompts and in what agents report — kinds, never values.
 *
 * A cloud key pasted into a prompt, a password in a commit message, a list of customers' emails:
 * each is recorded as what it was and how many, the moment it happens, for a manager to review.
 * What was actually typed never reaches this page — for prompts it never left the developer's
 * machine unless the organization keeps prompts, and then it was masked twice.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, ShieldAlert } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatDateTime } from '../lib/format'
import { Badge, EmptyState, Panel, Segmented, Skeleton, Stat } from '../components/primitives'
import type { SecurityFinding } from '../lib/types'

type Period = '24h' | '7d' | '30d'
type Minimum = 'Low' | 'Medium' | 'High'

const HOURS: Record<Period, number> = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 }

/** Where a finding was seen, in words: a prompt, a commit, a note — or the event's own name. */
function sourceKey(source: string): string | null {
  if (source === 'prompt') return 'security.source.prompt'
  if (source.startsWith('git.')) return 'security.source.commit'
  if (source === 'work.note' || source.startsWith('step.') || source.startsWith('work.')) return 'security.source.note'
  if (source.startsWith('document.')) return 'security.source.document'
  if (source.startsWith('test.')) return 'security.source.test'
  return null
}

function severityTone(severity: SecurityFinding['severity']): 'danger' | 'warn' | 'neutral' {
  return severity === 'High' ? 'danger' : severity === 'Medium' ? 'warn' : 'neutral'
}

export default function Security() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const [period, setPeriod] = useState<Period>('7d')
  const [minimum, setMinimum] = useState<Minimum>('Low')
  const [onlyOpen, setOnlyOpen] = useState(true)

  const { data: summary } = useQuery({ queryKey: ['security', 'summary'], queryFn: api.security.summary, refetchInterval: 10_000 })
  const { data: findings, isLoading } = useQuery({
    queryKey: ['security', 'findings', period, minimum, onlyOpen],
    queryFn: () => api.security.findings({
      from: new Date(Date.now() - HOURS[period] * 3600_000).toISOString(),
      severity: minimum,
      unreviewed: onlyOpen,
    }),
    // Near enough to live: a key pasted a moment ago is on the page within seconds.
    refetchInterval: 10_000,
  })

  const review = useMutation({
    mutationFn: (id: string) => api.security.review(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['security'] }),
  })

  return (
    <div className="stack stack--loose">
      <p className="row row--tight tiny muted" style={{ alignItems: 'flex-start', margin: 0 }}>
        <ShieldAlert size={13} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden /> {t('security.intro')}
      </p>

      <div className="grid grid--stats">
        <Stat label={t('security.openHigh')} value={summary?.unreviewedHigh ?? '—'} tone={summary && summary.unreviewedHigh > 0 ? 'danger' : undefined} />
        <Stat label={t('security.last24h')} value={summary ? summary.last24Hours.high + summary.last24Hours.medium + summary.last24Hours.low : '—'}
          foot={summary ? t('security.split', { high: summary.last24Hours.high, medium: summary.last24Hours.medium, low: summary.last24Hours.low }) : undefined} />
        <Stat label={t('security.last7d')} value={summary ? summary.last7Days.high + summary.last7Days.medium + summary.last7Days.low : '—'}
          foot={summary ? t('security.split', { high: summary.last7Days.high, medium: summary.last7Days.medium, low: summary.last7Days.low }) : undefined} />
        <Stat label={t('security.open')} value={summary?.unreviewed ?? '—'} />
      </div>

      <Panel
        title={t('security.findings')}
        actions={
          <div className="row row--wrap" style={{ gap: 'var(--s3)' }}>
            <Segmented<Period> value={period} options={(['24h', '7d', '30d'] as const).map((p) => ({ value: p, label: t(`security.period.${p}`) }))} onChange={setPeriod} />
            <Segmented<Minimum> value={minimum} options={(['Low', 'Medium', 'High'] as const).map((s) => ({ value: s, label: t(`security.min.${s}`) }))} onChange={setMinimum} />
            <label className="checkbox">
              <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />
              <span className="checkbox__text small">{t('security.onlyOpen')}</span>
            </label>
          </div>
        }
        flush
      >
        {isLoading ? (
          <Skeleton height={160} />
        ) : !findings || findings.length === 0 ? (
          <EmptyState title={t('security.none')} body={t('security.noneHint')} />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('security.when')}</th>
                  <th>{t('security.what')}</th>
                  <th>{t('security.who')}</th>
                  <th>{t('security.where')}</th>
                  <th className="right">{t('security.status')}</th>
                </tr>
              </thead>
              <tbody>
                {findings.map((finding) => (
                  <tr key={finding.id} className={finding.reviewedAt ? 'security__row--reviewed' : undefined}>
                    <td className="small mono">{formatDateTime(finding.detectedAt, locale)}</td>
                    <td>
                      <div className="row row--tight row--wrap">
                        <Badge tone={severityTone(finding.severity)}>{t(`security.severity.${finding.severity}`)}</Badge>
                        <span className="small bold">{t(`security.kind.${finding.kind}`)}</span>
                        {finding.count > 1 && <span className="tiny muted">×{finding.count}</span>}
                      </div>
                    </td>
                    <td className="small">
                      {finding.developerId ? <Link to={`/developers/${finding.developerId}/day`}>{finding.developerName ?? '—'}</Link> : '—'}
                    </td>
                    <td className="small">
                      <span title={finding.source}>{sourceKey(finding.source) ? t(sourceKey(finding.source)!) : finding.source}</span>
                      {finding.repository && <span className="mono muted"> · {finding.repository}</span>}
                      {finding.workSessionId && <> · <Link to={`/work/${finding.workSessionId}`}>{t('security.session')}</Link></>}
                    </td>
                    <td className="right">
                      {finding.reviewedAt ? (
                        <span className="tiny muted"><CheckCircle2 size={12} aria-hidden /> {t('security.reviewedBy', { name: finding.reviewedBy ?? '—' })}</span>
                      ) : (
                        <button type="button" className="btn btn--sm" onClick={() => review.mutate(finding.id)} disabled={review.isPending}>
                          {t('security.review')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  )
}
